import { FormValues } from '@approvalmax/types';
import { Popup } from '@approvalmax/ui';
import { Form, Grid, Spacing, Text, TextField } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { xeroConstants } from 'modules/common/constants';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { memo, useCallback, useEffect } from 'react';
import bemFactory from 'react-bem-factory';
import { useForm } from 'react-hook-form';
import { useUserBetaFeatures } from 'shared/hooks';
import { z } from 'zod';

import { cancelActivePopup, changeXeroDeliveryDetails, showXeroContactAddressSearchPopup } from '../../../actions';
import { XERO_CONTACT_ADDRESS_SEARCH_POPUP } from '../../../reducers/page/activePopup/xeroContactAddressSearchPopupReducer';
import { getActivePopupId, getShowValidationErrors } from '../../../selectors/pageSelectors';
import { getFieldsApprovalPermissionBySystemPurpose } from '../../../selectors/requestSelectors';
import { getXeroContext } from '../../../selectors/xero';
import { XeroContactAddressSearchPopup } from '../XeroContactAddressSearchPopup/XeroContactAddressSearchPopup';
import XeroContactAddressSearchPopupOld from '../XeroContactAddressSearchPopupOld/XeroContactAddressSearchPopup';
import { AddressDropdown } from './components';
import { messages } from './XeroDeliveryDetailsSection.messages';
import { createSchema } from './XeroDeliveryDetailsSection.schema';
import { XeroDeliveryDetailsSectionProps } from './XeroDeliveryDetailsSection.types';

const qa = bemFactory.qa('reqf-xero-delivery-details-section');

export const XeroDeliveryDetailsSection = memo<XeroDeliveryDetailsSectionProps>((props) => {
    const { request } = props;

    const dispatch = useDispatch();
    const showErrors = useSelector(getShowValidationErrors);
    const xeroContext = useSelector(getXeroContext);
    const activePopupId = useSelector(getActivePopupId);
    const [deliverFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroDeliveryAddress])
    );
    const { isBelgradeUIXeroContactAddressSearchPopup } = useUserBetaFeatures();
    const schema = createSchema(deliverFieldPermission.required);

    const form = useForm<z.infer<typeof schema>>({
        values: {
            address: request.details.delivery.address || '',
            attentionTo: request.details.delivery.attentionTo || '',
            phone: request.details.delivery.phone || '',
            instructions: request.details.delivery.instructions || '',
        },
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const instructions = form.watch('instructions');

    const handleFormChange = (values: FormValues<typeof form>) => {
        dispatch(changeXeroDeliveryDetails({ ...request.details.delivery, ...values }));
    };

    const handleCancelActivePopup = useCallback(() => dispatch(cancelActivePopup()), [dispatch]);
    const handleShowXeroContactAddressSearchPopup = useCallback(
        () => dispatch(showXeroContactAddressSearchPopup()),
        [dispatch]
    );

    /**
     * Trigger form validation when global showErrors is true
     */
    useEffect(() => {
        if (!showErrors) return;

        form.trigger();
    }, [form, showErrors]);

    return (
        <>
            <Form form={form} onChange={handleFormChange} data-qa={qa()}>
                <Grid gap={16} alignItems='center' gridTemplateColumns='max-content max-content'>
                    <Text font='headline' fontSize='xxsmall' fontWeight='medium' as='h2'>
                        {messages.deliveryAddressTitle}
                    </Text>

                    <AddressDropdown
                        addresses={xeroContext?.companyAddresses || []}
                        disabled={deliverFieldPermission.disabled}
                        onSearchClick={handleShowXeroContactAddressSearchPopup}
                    />
                </Grid>

                <Spacing height={16} />

                <Grid gap={16} gridTemplateColumns='1fr 1fr 2fr' gridTemplateRows='1fr'>
                    <TextField
                        name='address'
                        multiline
                        placeholder={messages.addressPlaceholder}
                        label={messages.addressLabel}
                        required={deliverFieldPermission.required}
                        disabled={deliverFieldPermission.disabled}
                        size='small'
                        maxLength={xeroConstants.DELIVERY_ADDRESS_MAX_LENGTH}
                        fullHeight
                        qa={qa('address-editor')}
                    />

                    <Grid gap={16}>
                        <TextField
                            name='attentionTo'
                            placeholder={messages.attentionPlaceholder}
                            label={messages.attentionLabel}
                            disabled={deliverFieldPermission.disabled}
                            size='small'
                            maxLength={xeroConstants.DELIVERY_ATTENTION_TO_MAX_LENGTH}
                            qa={qa('attention-to-editor')}
                        />

                        <TextField
                            name='phone'
                            placeholder={messages.phonePlaceholder}
                            label={messages.phoneLabel}
                            disabled={deliverFieldPermission.disabled}
                            size='small'
                            maxLength={xeroConstants.DELIVERY_PHONE_MAX_LENGTH}
                            qa={qa('delivery-details-editor')}
                        />
                    </Grid>

                    <TextField
                        multiline
                        name='instructions'
                        placeholder={messages.deliveryInstructionsPlaceholder}
                        label={messages.deliveryInstructionsLabel}
                        disabled={deliverFieldPermission.disabled}
                        size='small'
                        maxLength={xeroConstants.DELIVERY_INSTRUCTIONS_MAX_LENGTH}
                        fullHeight
                        hint={`${xeroConstants.DELIVERY_INSTRUCTIONS_MAX_LENGTH - instructions.length} / ${xeroConstants.DELIVERY_INSTRUCTIONS_MAX_LENGTH}`}
                        qa={qa('instructions-editor')}
                    />
                </Grid>
            </Form>

            {isBelgradeUIXeroContactAddressSearchPopup ? (
                <XeroContactAddressSearchPopup
                    open={activePopupId === XERO_CONTACT_ADDRESS_SEARCH_POPUP}
                    onClose={handleCancelActivePopup}
                />
            ) : (
                <Popup
                    isOpen={activePopupId === XERO_CONTACT_ADDRESS_SEARCH_POPUP}
                    onRequestClose={handleCancelActivePopup}
                >
                    <XeroContactAddressSearchPopupOld />
                </Popup>
            )}
        </>
    );
});

XeroDeliveryDetailsSection.displayName = 'XeroDeliveryDetailsSection';
